import { scaleTime, type ScaleLinear } from "d3";
import { differenceInMonths, format } from "date-fns";

type AxisBottomProps = {
  xScale: ScaleLinear<number, number>;
  width: number;
  height: number;
};

const TICK_LENGTH = 6;

export const AxisBottom = ({ xScale, width, height }: AxisBottomProps) => {
  const numberOfTicks = width > 750 ? 10 : 5;
  const domain = scaleTime().domain(xScale.domain());

  // Calculate the time range in months
  const monthsDiff = differenceInMonths(domain.domain()[1], domain.domain()[0]);

  // Determine format based on time range
  const getTickFormat = (date: Date) => {
    if (monthsDiff > 36) {
      return format(date, "yyyy"); // Years only
    } else {
      return format(date, "MMM yyyy"); // Months (Jan 2023)
    }
  };

  // Adjust number of ticks based on range
  const getTickCount = () => {
    if (monthsDiff > 36) {
      return Math.min(numberOfTicks, monthsDiff / 12); // Yearly ticks
    } else if (monthsDiff > 12) {
      return Math.min(numberOfTicks, monthsDiff / 3); // Quarterly ticks
    }
    return numberOfTicks; // Monthly ticks
  };

  const ticks = domain.ticks(getTickCount()).map((value) => ({
    value,
    xOffset: xScale(value),
  }));

  return (
    <>
      {ticks.map(({ value, xOffset }, i) => (
        <g key={i} transform={`translate(${xOffset}, 0)`} shapeRendering={"crispEdges"}>
          <line
            className="stroke-border"
            y1={TICK_LENGTH}
            y2={-height - TICK_LENGTH}
            stroke="current"
            strokeWidth={0.5}
          />
          <text
            className="translate-y-6 fill-muted-foreground text-xs"
            style={{
              textAnchor: "middle",
            }}
          >
            {getTickFormat(value)}
          </text>
        </g>
      ))}
    </>
  );
};
